/**
 * Deterministic pseudo-random numbers.
 *
 * The requirement here is reproducibility, not unpredictability: the same exam, filters and seed
 * must draw the same questions in the same order, on any machine, in any browser, years apart.
 * `Math.random` cannot do that, and a cryptographic generator would be the wrong tool — nobody is
 * trying to predict which questions they get.
 *
 * That distinction also decides the seed derivation. The design review proposed SHA-256, which is
 * asynchronous in the browser and would make blueprint generation — and therefore every caller of
 * it — asynchronous too, for a mixing step with no security role. A synchronous integer hash keeps
 * the whole engine pure and testable, and is exactly as deterministic.
 */

export interface Prng {
  /** A float in [0, 1). */
  next(): number;
  /** An integer in [0, bound). */
  nextInt(bound: number): number;
}

/**
 * cyrb128 — turns a string into four well-mixed 32-bit words.
 *
 * Chosen over "sum the char codes" style seeding because a weak seed makes xoshiro's first outputs
 * correlate: two exams whose hashes differ in one character would otherwise start with a similar
 * shuffle.
 */
export function seedWordsFrom(text: string): [number, number, number, number] {
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let h4 = 2773480762;

  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    h1 = h2 ^ Math.imul(h1 ^ code, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ code, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ code, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ code, 2716044179);
  }

  return [
    (Math.imul(h3 ^ (h1 >>> 18), 597399067) ^ Math.imul(h4 ^ (h2 >>> 22), 2869860233)) >>> 0,
    (Math.imul(h4 ^ (h2 >>> 22), 951274213) ^ Math.imul(h1 ^ (h3 >>> 17), 2716044179)) >>> 0,
    (Math.imul(h1 ^ (h3 >>> 17), 2869860233) ^ Math.imul(h2 ^ (h4 >>> 19), 597399067)) >>> 0,
    (Math.imul(h2 ^ (h4 >>> 19), 2716044179) ^ Math.imul(h3 ^ (h1 >>> 18), 951274213)) >>> 0,
  ];
}

const rotateLeft = (value: number, bits: number): number =>
  ((value << bits) | (value >>> (32 - bits))) >>> 0;

/**
 * xoshiro128** — small, fast, and with a long period and good statistical properties.
 *
 * The algorithm is pinned by name in every exported session (`blueprint.algorithm`), so a future
 * version can still reproduce a session recorded today even if it changes generator.
 */
export function createPrng(seed: readonly [number, number, number, number]): Prng {
  let [s0, s1, s2, s3] = seed;

  // An all-zero state is a fixed point that only ever produces zero.
  if ((s0 | s1 | s2 | s3) === 0) {
    s0 = 1;
  }

  const nextUint32 = (): number => {
    const result = (Math.imul(rotateLeft(Math.imul(s1, 5) >>> 0, 7), 9) >>> 0) >>> 0;
    const t = (s1 << 9) >>> 0;

    s2 = (s2 ^ s0) >>> 0;
    s3 = (s3 ^ s1) >>> 0;
    s1 = (s1 ^ s2) >>> 0;
    s0 = (s0 ^ s3) >>> 0;
    s2 = (s2 ^ t) >>> 0;
    s3 = rotateLeft(s3, 11);

    return result;
  };

  return {
    next: (): number => nextUint32() / 4294967296,
    nextInt: (bound: number): number => {
      if (bound <= 0) {
        return 0;
      }
      // Rejection sampling: taking a modulo directly biases the low values, which over a whole
      // question bank is a visible skew rather than a theoretical one.
      const limit = Math.floor(4294967296 / bound) * bound;
      let value = nextUint32();
      while (value >= limit) {
        value = nextUint32();
      }
      return value % bound;
    },
  };
}

export function prngFrom(text: string): Prng {
  return createPrng(seedWordsFrom(text));
}
